// Read-only grouping metadata from GET /ra-screening/:runId/systems.
// Raw rows remain the source of truth for every review action/citation.
export interface ScreeningReviewGroup {
  id: string
  display_name: string
  member_ids: string[]
  kind: 'single' | 'repeated_mentions' | 'numbered_units'
  units: number[]
  context_varies?: boolean
}

export interface ScreeningReviewItem<T> {
  id: string
  display_name: string
  kind: ScreeningReviewGroup['kind']
  units: number[]
  /** Visible raw members only, each retaining its own drawing and evidence. */
  members: T[]
  /** Full server membership, useful to label a partially filtered group. */
  total_members: number
  context_varies: boolean
}

/**
 * Apply AFTER raw-row filters. Older API responses fall back to single rows.
 * Never manufacture a merged ScreeningSystemRow or use an item id in PATCH.
 * Group-level actions, if offered, must explicitly act on members' raw ids.
 * Reload systems after a review action so changed assessments regroup safely.
 */
export function screeningReviewItems<T extends { id: string; system_name: string; review_group?: ScreeningReviewGroup }>(rows: readonly T[]): ScreeningReviewItem<T>[] {
  const items = new Map<string, ScreeningReviewItem<T>>()
  for (const row of rows) {
    const group = row.review_group?.member_ids.includes(row.id) ? row.review_group : undefined
    const id = group ? group.id : `row:${row.id}`
    let item = items.get(id)
    if (!item) {
      item = {
        id,
        display_name: group ? group.display_name : row.system_name,
        kind: group ? group.kind : 'single',
        units: group ? group.units : [],
        members: [],
        total_members: group ? group.member_ids.length : 1,
        context_varies: group?.context_varies === true,
      }
      items.set(id, item)
    }
    item.members.push(row)
  }
  return [...items.values()]
}
